import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import s from "./Pagination.module.scss";
import { FirstPage, LastPage } from "@mui/icons-material";

const Pagination = ({
  pagination,
  setCurrentPage,
  setPageSize,
  pageSizeInfoRef,
  handleSearch,
  tableOnChange,
  total,
}) => {
  const { t } = useTranslation();
  const { currentPage, pageSize, pageSizeOptions } = pagination;
  const [gotoPageOnFocus, setGotoPageOnFocus] = useState(false);
  const [page, setPage] = useState(currentPage + 1);

  useEffect(() => {
    setPage(currentPage + 1);
  }, [currentPage]);

  const recordFrom = currentPage * pageSize + 1;
  let recordTo;
  if ((currentPage + 1) * pageSize > total) {
    recordTo = total;
  } else {
    recordTo = (currentPage + 1) * pageSize;
  }

  const selectSx = {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
      outline: "none",
    },
    "&.MuiOutlinedInput-root": {
      height: "32px",
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "14px",
      // height: "14px",
      // minHeight: "12px",
      // transform: "translateY(-3px)",
    },
    "& .MuiSvgIcon-root": {
      color: "#309ea7",
    },
  };

  const menuSx = {
    "&.MuiMenuItem-root": {
      fontSize: 14,
    },
  };

  const inputHeight = "32px";
  const focusColor = "#36b3be";
  const regularColor = "#ddd";

  const outlinedTextSx = {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
      outline: "none",
    },
    "&.MuiOutlinedInput-root": {
      borderRadius: "4px",
      border: gotoPageOnFocus
        ? `1px solid ${focusColor}`
        : `1px solid ${regularColor}`,
      height: inputHeight,
      width: "64px",
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "14px",
    },
  };

  const handleGotoPageOnFocus = () => {
    setGotoPageOnFocus(true);
    return;
  };
  const handleGotoPageOnBlur = () => {
    setGotoPageOnFocus(false);
    return;
  };
  const handleGotoPageOnInput = (e) => {
    let { value } = e.target;
    value = value.replace(/\D/g, "");
    setPage(value);
    return;
  };
  const handlePressEnterKey = (e) => {
    if (e.key !== "Enter") {
      return;
    }
    e.preventDefault();
    if (page - 1 < 0) {
      setPage(1);
      return paginationHandler({ value: 0 }, "gotoPage");
    }
    const maxPage = Math.ceil(total / pageSize);
    if (page > maxPage) {
      setPage(maxPage);
      return paginationHandler({ value: maxPage - 1 }, "gotoPage");
    }
    return paginationHandler({ value: page - 1 }, "gotoPage");
  };

  const paginationHandler =
    tableOnChange ??
    ((e, action) => {
      if (action === "first") {
        if (currentPage - 1 < 0) {
          return;
        }
        setCurrentPage(0);
        pageSizeInfoRef.current.currentpage = 0;
      }
      if (action === "previous") {
        if (currentPage - 1 < 0) {
          return;
        }
        setCurrentPage((currentPage) => currentPage - 1);
        pageSizeInfoRef.current.currentpage = currentPage - 1;
      }
      if (action === "next") {
        setCurrentPage((currentPage) => currentPage + 1);
        pageSizeInfoRef.current.currentpage = currentPage + 1;
      }
      if (action === "last") {
        const lastPageNumber = Math.ceil(total / pageSize) - 1;
        setCurrentPage(lastPageNumber);
        pageSizeInfoRef.current.currentpage = lastPageNumber;
      }
      if (action === "changePageSize") {
        const { value } = e.target;
        setPageSize(value);
        pageSizeInfoRef.current.pagesize = value;
        setCurrentPage(0);
        pageSizeInfoRef.current.currentpage = 0;
      }
      if (action === "gotoPage") {
        const { value } = e;
        setCurrentPage(value);
        pageSizeInfoRef.current.currentpage = value;
      }
      return handleSearch();
    });

  return (
    <div className={s.paginationContainer}>
      <div className={s.pageSizeSelection}>
        <span className={s.pageSizeLabel}>
          {t("component.pagination.rows.per.page")}
        </span>
        <Select
          value={pageSize}
          onChange={(e) => paginationHandler(e, "changePageSize")}
          sx={selectSx}
        >
          {pageSizeOptions.map((option, index) => {
            return (
              <MenuItem key={index} value={option} sx={menuSx}>
                {option}
              </MenuItem>
            );
          })}
        </Select>
      </div>
      <div className={s.showingRecord}>
        {`${recordFrom}-${recordTo} of ${total}`}
      </div>
      <div className={s.pageSelection}>
        <button
          className={s.pageChangingBtn}
          onClick={(e) => paginationHandler(e, "first")}
          disabled={currentPage === 0}
        >
          <FirstPage sx={{ fontSize: 24 }} />
        </button>
        <button
          className={s.pageChangingBtn}
          onClick={(e) => paginationHandler(e, "previous")}
          disabled={currentPage === 0}
        >
          <ChevronLeftIcon sx={{ fontSize: 24 }} />
        </button>
        <button
          className={s.pageChangingBtn}
          onClick={(e) => paginationHandler(e, "next")}
          disabled={(currentPage + 1) * pageSize >= total}
        >
          <ChevronRightIcon sx={{ fontSize: 24 }} />
        </button>
        <button
          className={s.pageChangingBtn}
          onClick={(e) => paginationHandler(e, "last")}
          disabled={(currentPage + 1) * pageSize >= total}
        >
          <LastPage sx={{ fontSize: 24 }} />
        </button>
      </div>

      <div className={s.gotoPage}>
        <span className={s.pageSizeLabel}>
          {t("component.pagination.go.to.page")}
        </span>
        <OutlinedInput
          value={page}
          onInput={handleGotoPageOnInput}
          onKeyDown={handlePressEnterKey}
          onFocus={handleGotoPageOnFocus}
          onBlur={handleGotoPageOnBlur}
          sx={outlinedTextSx}
        />
      </div>
    </div>
  );
};

export default React.memo(Pagination);
